import { AppError, ErrorCode } from "../shared/errors/app-error.js";
import type { PermissionCode, RoleCode } from "./permissions.js";

export interface AuthorizationContext {
  readonly permissions?: ReadonlySet<PermissionCode> | Set<string>;
  readonly role?: RoleCode | string;
  readonly userId?: string;
  readonly professionalId?: string | null;
  readonly activeClientId?: string | null;
}

export interface AuthorizationService {
  can(context: AuthorizationContext, permission: PermissionCode): boolean;
  require(context: AuthorizationContext, permission: PermissionCode): void;
  canAny(context: AuthorizationContext, permissions: readonly PermissionCode[]): boolean;
  requireAny(context: AuthorizationContext, permissions: readonly PermissionCode[]): void;
}

export class RbacAuthorizationService implements AuthorizationService {
  /**
   * A decisão usa APENAS as permissões do contexto recebido na chamada.
   *
   * Havia aqui um segundo caminho: se o contexto da chamada não tivesse a
   * permissão, a resposta era buscada em `window.contextStore`. Isso anulava
   * qualquer restrição de contexto — um serviço chamado com um contexto
   * propositalmente reduzido (o do próprio usuário, o de um job) recebia de
   * volta as permissões da sessão global do navegador. Quem chama é
   * responsável por montar o contexto certo; contexto incompleto é negação.
   */
  public can(context: AuthorizationContext, permission: PermissionCode): boolean {
    if (context && context.permissions && typeof context.permissions.has === "function") {
      return context.permissions.has(permission as any);
    }
    return false;
  }

  public canAny(context: AuthorizationContext, permissions: readonly PermissionCode[]): boolean {
    return permissions.some((perm) => this.can(context, perm));
  }

  public require(context: AuthorizationContext, permission: PermissionCode): void {
    if (!this.can(context, permission)) {
      throw new AppError(
        ErrorCode.Authorization,
        `Permission denied: ${permission}`,
        {
          safeMessage: `Acesso negado: você não possui a permissão '${permission}' necessária para realizar esta operação.`
        }
      );
    }
  }

  public requireAny(context: AuthorizationContext, permissions: readonly PermissionCode[]): void {
    if (!this.canAny(context, permissions)) {
      throw new AppError(
        ErrorCode.Authorization,
        `Permission denied: requires one of ${permissions.join(", ")}`,
        {
          safeMessage: `Acesso negado: você não possui nenhuma das permissões necessárias (${permissions.join(", ")}) para realizar esta operação.`
        }
      );
    }
  }
}

